/**
 * Formats a Date object into a `YYYY-MM-DD` string suitable for API query parameters.
 * Uses local time to avoid UTC offset issues where dates near midnight could
 * shift to the previous or next day when converted to UTC.
 *
 * @param date - The Date object to format
 * @returns A string in `YYYY-MM-DD` format (e.g. `"2025-03-20"`)
 *
 * @example
 * formatDateOnly(new Date('2025-03-20')) // "2025-03-20"
 */
export function formatDateOnly(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Formats a Date object into a human-readable `MM/DD/YY` string for display purposes.
 * Uses the `en-US` locale to ensure consistent formatting regardless of the user's
 * browser locale settings.
 *
 * @param date - The Date object to format
 * @returns A string in `MM/DD/YY` format (e.g. `"03/20/25"`)
 *
 * @example
 * formatDateDisplay(new Date('2025-03-20')) // "03/20/25"
 *
 * @remarks
 * This is intended for display only — do not use this format for API calls or
 * date comparisons. Use `formatDateOnly` instead for API query parameters.
 */
export function formatDateDisplay(date: Date): string {
    return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
    });
}

/**
 * Calculates the start of the week for a given date (Monday).
 *
 * @param date - Source date to calculate from.
 * @returns A new Date object set to Monday 00:00:00 of the same week.
 */
export function startOfWeek(date: Date): Date {
    const clone = new Date(date);
    const day = clone.getDay();
    const diff = (day + 6) % 7;
    clone.setHours(0, 0, 0, 0);
    clone.setDate(clone.getDate() - diff);
    return clone;
}

/**
 * Sets the time of a date to the very end of the day (23:59:59.999).
 *
 * @param date - Source date to modify.
 * @returns A new Date object set to the end of the same day.
 */
export function endOfDay(date: Date): Date {
    const clone = new Date(date);
    clone.setHours(23, 59, 59, 999);
    return clone;
}

/**
 * Adds or subtracts a specified number of days from a date.
 *
 * @param date - Source date.
 * @param days - Number of days to add (can be negative).
 * @returns A new Date object with the adjusted date.
 */
export function addDays(date: Date, days: number): Date {
    const clone = new Date(date);
    clone.setDate(clone.getDate() + days);
    return clone;
}

/**
 * Builds a UTC ISO date string from discrete date parts.
 *
 * @param year - Four-digit year.
 * @param monthIndex - 0-based month index.
 * @param day - Day of the month.
 * @param hours - UTC hours.
 * @param minutes - UTC minutes.
 * @param seconds - UTC seconds.
 * @param milliseconds - UTC milliseconds.
 * @returns ISO-formatted UTC date string.
 */
export function toUtcIso(
    year: number,
    monthIndex: number,
    day: number,
    hours = 0,
    minutes = 0,
    seconds = 0,
    milliseconds = 0,
): string {
    return new Date(
        Date.UTC(year, monthIndex, day, hours, minutes, seconds, milliseconds),
    ).toISOString();
}

/**
 * Formats a given year and month index into a short UTC month string (e.g., "Jan").
 *
 * @param year - The year.
 * @param monthIndex - 0-based month index.
 * @returns Formatted month string in UTC.
 */
export function formatUtcMonth(year: number, monthIndex: number): string {
    return new Date(Date.UTC(year, monthIndex, 1)).toLocaleString('en-US', {
        month: 'short',
        timeZone: 'UTC',
    });
}

/**
 * Formats an ISO date string into a long human-readable date.
 * (e.g., "October 14, 2025")
 *
 * @param date - ISO date string or Date object
 * @returns A formatted date string with full month name.
 */
export function formatDateLong(date: string | Date): string {
    return new Date(date).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Formats an ISO date string into a human-readable activity description date.
 * (e.g., "Mar 20, 2025, 14:00")
 *
 * @param createdAt - The ISO creation date.
 * @returns A formatted description string.
 */
export function formatDateTime(createdAt: string): string {
    return new Date(createdAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

/**
 * Formats an ISO date string into a human-readable time string.
 * (e.g., "02:00 PM")
 *
 * @param date - The ISO creation date.
 * @returns A formatted time string.
 */
export function formatTime(date: string): string {
    return new Date(date).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}
